import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { produce } from 'immer'
import { TimeWindow } from '@dhl/inputs'
import { ACTION, Action } from '~/basket/model/capabilities/actions'
import {
  PickupRangeBound,
  SelectableDeliveryOption,
  SelectableHandOffOption,
  SelectableServiceOption,
} from '~/basket/model/reducers'
import { Price, cheapest, zero } from '~/basket/model/utils/price'
import { ServiceExpressEconomyQuery, ServiceExpressQuery, ServiceParcelQuery } from '~/gql'
import { DAY_EVENING_THRESHOLD, OPTION_TYPE } from './constants'

import { CARRIER } from '~/basket/model/constants'
import { convertDateToRoundedDecimalTime } from '~/basket/view/util/dateTime'
import { sortBy } from '~/util/helpers/sortBy'

dayjs.extend(customParseFormat)

/** *****************
 * Reducers
 ***************** */

// eslint-disable-next-line @typescript-eslint/naming-convention
type serviceQuery$data = ServiceExpressQuery | ServiceParcelQuery | ServiceExpressEconomyQuery

export type CapabilityState = {
  data: serviceQuery$data | null
  error: Error | null
  isLoading: boolean
}

const initialState: CapabilityState = {
  data: null,
  error: null,
  isLoading: true,
}

// eslint-disable-next-line default-param-last
export const capabilityReducer = produce((draft = initialState, action: Action): CapabilityState => {
  switch (action.type) {
    case ACTION.UPDATE_CAPABILITIES_REQUESTED:
      return {
        ...draft,
        error: null,
        isLoading: action.payload.isLoading,
      }
    case ACTION.UPDATE_CAPABILITIES_SUCCESS:
      return {
        error: null,
        isLoading: false,
        data: {
          ...draft.data,
          ...action.payload.capabilities,
        },
      }
    case ACTION.UPDATE_CAPABILITIES_FAILURE:
      return {
        error: action.payload.error,
        data: {
          ...draft.data,
        },
        isLoading: false,
      }
    default: {
      return draft
    }
  }
})

/** *****************
 * Selectors
 ***************** */

const isLoadingCapability = (state: CapabilityState): boolean => state.isLoading

const getError = (state: CapabilityState): Error | null => state.error

const getData = (state: CapabilityState): serviceQuery$data => {
  if (state.data) {
    return state.data
  }

  throw new Error('No data found, capabilities not loaded')
}

const getDataByCarrier = (state: CapabilityState) => (carrierKey: string) => {
  switch (carrierKey) {
    case CARRIER.EXPRESS:
      // eslint-disable-next-line no-case-declarations
      const expressResponse = getData(state) as ServiceExpressQuery
      return expressResponse.express
    case CARRIER.EXPRESS_ECONOMY:
      // eslint-disable-next-line no-case-declarations
      const expressEconomyResponse = getData(state) as ServiceExpressEconomyQuery
      return expressEconomyResponse.expressEconomy
    case CARRIER.PARCEL:
      // eslint-disable-next-line no-case-declarations
      const parcelResponse = getData(state) as ServiceParcelQuery
      return parcelResponse.parcel
    default:
      throw new Error(`Unknown carrierKey: ${carrierKey}`)
  }
}

const hasCapabilities = (state: CapabilityState) => state?.data

const doesCarrierShipToCountry =
  (state: CapabilityState) =>
  (carrierKey: string, destinationCountry: string): boolean => {
    const data = getDataByCarrier(state)(carrierKey)
    if (data) {
      return data.destinationCountries.find(({ code }) => code === destinationCountry) !== undefined
    }
    return false
  }

const getMinimumPriceCarrier =
  (state: CapabilityState) =>
  (carrierKey: string): Price => {
    const prices = getDataByCarrier(state)(carrierKey).parcelTypes.map((parcelType) => parcelType.price!)

    return prices.length > 0 ? cheapest(prices) : zero
  }

const isParcelTypeSelectable =
  (state: CapabilityState) =>
  (carrierKey: string, parcelTypeKey: string): boolean =>
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    getParcelTypesByCarrier(state)(carrierKey).some((parcelType) => parcelType.key === parcelTypeKey)

const getDestinationCountriesByCarrier = (state: CapabilityState) => (carrierKey: string, originCountry: string) => {
  const mapToIsPopularAndOrdering = (code: string) => {
    switch (originCountry) {
      case 'NL':
        switch (code) {
          case 'NL':
            return { isPopular: true, ordering: 1 }
          case 'DE':
            return { isPopular: true, ordering: 2 }
          case 'BE':
            return { isPopular: true, ordering: 3 }
          case 'GB':
            return { isPopular: true, ordering: 4 }
          case 'FR':
            return { isPopular: true, ordering: 5 }
          default:
            return { isPopular: false }
        }
      case 'PT':
        switch (code) {
          case 'PT':
            return { isPopular: true, ordering: 1 }
          case 'ES':
            return { isPopular: true, ordering: 2 }
          case 'DE':
            return { isPopular: true, ordering: 3 }
          default:
            return { isPopular: false }
        }
      case 'ES':
        switch (code) {
          case 'ES':
            return { isPopular: true, ordering: 1 }
          case 'PT':
            return { isPopular: true, ordering: 2 }
          case 'DE':
            return { isPopular: true, ordering: 3 }
          default:
            return { isPopular: false }
        }
      default:
        return { isPopular: false }
    }
  }
  if (getDataByCarrier(state)(carrierKey)) {
    return getDataByCarrier(state)(carrierKey).destinationCountries.map(({ code }) => {
      const { isPopular, ordering } = mapToIsPopularAndOrdering(code)

      if (isPopular) {
        return {
          code,
          isPopular,
          ordering,
        }
      }
      return {
        code,
        isPopular,
      }
    })
  }
  return []
}

const getParcelTypesByCarrier = (state: CapabilityState) => (carrierKey: string) =>
  getDataByCarrier(state)(carrierKey).parcelTypes

const getParcelTypeByCarrierAndParcelTypeKey =
  (state: CapabilityState) => (carrierKey: string, parcelTypeKey: string) => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const parcelType = getParcelTypesByCarrier(state)(carrierKey).find((parcelType) => parcelType.key === parcelTypeKey)

    if (parcelType) {
      return parcelType
    }

    throw new Error(`Parcel type for carrier key ${carrierKey} and parcel type key ${parcelTypeKey} does not exist`)
  }

const isCustomsDestinationCountry = (state: CapabilityState): boolean =>
  getData(state).capabilitiesContainer.isCustomsDestinationQuery

const getProductKey =
  (state: CapabilityState) =>
  (carrierKey: string): string | undefined =>
    getDataByCarrier(state)(carrierKey).capabilities[0]?.productKey

const normalizePrice = (price: Price | null | undefined): Price => {
  if (price === null) {
    return {
      withTax: 0,
      withoutTax: 0,
    }
  }

  return price!
}

const getDeliveryOptionsByCarrier =
  (state: CapabilityState) =>
  (carrierKey: string): SelectableDeliveryOption[] => {
    const options = getDataByCarrier(state)(carrierKey).capabilities.flatMap((capability) =>
      capability.options.filter((option) => option.optionType === OPTION_TYPE.DELIVERY_OPTION)
    )

    return options.map((option) => ({
      excludes: option.excludes,
      implies: option.implies,
      key: option.key,
      price: normalizePrice(option.price),
    }))
  }

type Predicate<A> = (a: A) => boolean

const getServiceOptionsByCarrier =
  (state: CapabilityState) =>
  (carrierKey: string, exclusions: string[]): SelectableServiceOption[] => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isServiceOption: Predicate<any> = (option) => option.optionType === OPTION_TYPE.SERVICE_OPTION

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isExcluded: Predicate<any> = (option) => {
      if (option.exclusions === null) {
        return false
      }

      // @ts-ignore
      return exclusions.some((exclusion) => option.exclusions.map(({ key }) => key).includes(exclusion))
    }

    function not<A>(fn: Predicate<A>): Predicate<A> {
      return (a: A) => !fn(a)
    }

    const options = getDataByCarrier(state)(carrierKey)
      .capabilities.flatMap((capability) => capability.options.filter(isServiceOption))
      .filter(not(isExcluded))

    return options.map((option) => ({
      excludes: option.excludes,
      implies: option.implies,
      key: option.key,
      price: normalizePrice(option.price),
    }))
  }

const getHandOffOptionsByCarrierAndShipment =
  (state: CapabilityState) =>
  (carrierKey: string): SelectableHandOffOption[] => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isHandOffOption: Predicate<any> = (option) => option.optionType === OPTION_TYPE.HAND_OFF_OPTION

    const options = getDataByCarrier(state)(carrierKey)
      .capabilities.flatMap((capability) => capability.options)
      .filter(isHandOffOption)

    return options.map((option) => ({
      key: option.key,
      price: normalizePrice(option.price),
    }))
  }

const getPickupRangeBoundByCarrier =
  (state: CapabilityState) =>
  (carrierKey: string, pickupDate: string | null = null): PickupRangeBound | null => {
    const { handOffDates } = getDataByCarrier(state)(carrierKey)
    if (!handOffDates) {
      return null
    }
    const result = pickupDate
      ? handOffDates.handOffDates.find(({ date }) => date === pickupDate)
      : handOffDates.handOffDates[0]

    if (!result) {
      return null
    }

    const { pickupRangeBound } = result

    if (!pickupRangeBound) {
      return null
    }

    return {
      min: convertDateToRoundedDecimalTime(new Date(pickupRangeBound.min)),
      max: convertDateToRoundedDecimalTime(new Date(pickupRangeBound.max)),
    }
  }

const getPickupDatesByCarrier =
  (state: CapabilityState) =>
  (carrierKey: string): string[] => {
    const { handOffDates } = getDataByCarrier(state)(carrierKey)
    if (!handOffDates) {
      return []
    }

    return handOffDates.handOffDates.map(({ date }) => date)
  }

const getTransitDaysByCarrierAndShipment =
  (state: CapabilityState) =>
  (carrierKey: string, pickupDate: string | null = null): number | null => {
    const { handOffDates } = getDataByCarrier(state)(carrierKey)
    if (!handOffDates) {
      return null
    }

    const result = pickupDate
      ? handOffDates.handOffDates.find(({ date }) => date === pickupDate)
      : handOffDates.handOffDates[0]

    if (!result) {
      return null
    }

    return result.days.standard
  }

const getExpectedDeliveryDateByCarrierAndShipment =
  (state: CapabilityState) =>
  (carrierKey: string, pickupDate: string | null = null) => {
    const { handOffDates } = getDataByCarrier(state)(carrierKey)
    if (!handOffDates) {
      return null
    }

    const result = pickupDate
      ? handOffDates.handOffDates.find(({ date }) => date === pickupDate)
      : handOffDates.handOffDates[0]

    if (!result) {
      return null
    }

    return result.expectedDeliveryDate
  }

const getTimeWindows =
  (state: CapabilityState) =>
  (carrierKey: string): TimeWindow[] => {
    const { timeWindows } = getDataByCarrier(state)(carrierKey)

    if (!timeWindows) {
      return []
    }

    return sortBy(
      timeWindows
        .map((timeWindow) => ({
          start: dayjs(`${timeWindow.deliveryDate} ${timeWindow.startTime}`, 'DD-MM-YYYY Hmm'),
          end: dayjs(`${timeWindow.deliveryDate} ${timeWindow.endTime}`, 'DD-MM-YYYY Hmm'),
        }))
        .map(({ start, end }) => ({
          start,
          end,
          type: start.hour() < DAY_EVENING_THRESHOLD ? 'day' : 'evening',
        })),
      ({ start }) => start.unix()
    )
  }

export const capabilitySelectors = {
  isLoadingCapability,
  getError,
  doesCarrierShipToCountry,
  isParcelTypeSelectable,
  getDeliveryOptionsByCarrier,
  getServiceOptionsByCarrier,
  getHandOffOptionsByCarrierAndShipment,
  getMinimumPriceCarrier,
  getParcelTypesByCarrier,
  getDestinationCountriesByCarrier,
  getPickupDatesByCarrier,
  getPickupRangeBoundByCarrier,
  getTransitDaysByCarrierAndShipment,
  getExpectedDeliveryDateByCarrierAndShipment,
  getParcelTypeByCarrierAndParcelTypeKey,
  getProductKey,
  hasCapabilities,
  isCustomsDestinationCountry,
  getTimeWindows,
}
